import { useCallback, useEffect, useRef, useState } from "react"

import { selectActiveBranchId } from "../../../Branches/redux/branches.slice"
import { getPendingContracts } from "../../../Contracts/api/services"
import { getTracking } from "../../../Esign/api/tracking.services"
import { getMyPendingReviews } from "../../../Project/api/project-attachment-workflow.services"
import { getPendingProposals } from "../../../Proposals/api/services"
import { RoutesPaths } from "../../../../constants/routes"
import { useAppSelector } from "../../../../hooks/use-app-selector"

export type ApprovalKind = "proposal" | "contract" | "review" | "signature"

export interface HomeApprovalRow {
  id: string
  kind: ApprovalKind
  /** The record it is about — a lead, a project, a document. */
  entity: string
  /** Secondary context: customer, project, who sent it. */
  context: string | null
  /** ISO-ish instant the row is ordered by (oldest waiting first). */
  since: string | null
  /** Where the action happens. */
  to: string
}

export interface HomeApprovalsData {
  rows: HomeApprovalRow[]
  total: number
  state: "loading" | "ready" | "empty" | "error"
  retry: () => void
}

const LIMIT = 3

/**
 * Everything waiting on this user: proposals and contracts to approve,
 * documents to review, and signature requests still out.
 *
 * No dashboard data source lists these, so the card reads the four
 * authorized endpoints the Approval pages and Signature Manager already use
 * — the same rows those screens show, gated the same way
 * (`notification-counts.thunks` is the precedent: a page the user does not
 * hold is not asked). Signature Manager has no page slug of its own, so that
 * call is made and a 401/403 is read as "not permitted" rather than as an
 * error. Every call carries the branch (`team`), and a response that lands
 * after the branch changed is dropped, so a branch switch never leaves the
 * previous branch's approvals on screen.
 */
export const useHomeApprovals = (): HomeApprovalsData => {
  const pages = useAppSelector((s) => s.auth?.permissions?.pages ?? [])
  const branchId = useAppSelector(selectActiveBranchId)
  const [rows, setRows] = useState<HomeApprovalRow[]>([])
  const [total, setTotal] = useState(0)
  const [state, setState] = useState<HomeApprovalsData["state"]>("loading")
  const [attempt, setAttempt] = useState(0)
  const requestRef = useRef(0)

  const mayProposals = pages.includes("proposals")
  const mayContracts = pages.includes("contracts")
  const mayReviews = pages.includes("document_reviews")

  useEffect(() => {
    const id = ++requestRef.current
    let cancelled = false
    setState("loading")
    const team = branchId ?? undefined

    const run = async () => {
      const collected: HomeApprovalRow[] = []
      let failed = false

      // Every source answers with some `{ ok, status, data }` — apisauce's
      // `ApiResponse` for three of them, the esign `loaded()` outcome for the
      // fourth — so the outcome is read loosely and the row mapper narrows.
      type Outcome = { ok?: boolean; status?: number | null; data?: unknown }
      const settle = async (
        allowed: boolean,
        call: () => Promise<unknown>,
        map: (data: unknown) => HomeApprovalRow[]
      ) => {
        if (!allowed) return
        try {
          const response = (await call()) as Outcome
          if (response.ok && response.data != null) {
            collected.push(...map(response.data))
            return
          }
          // A refusal is a permission answer, not a failure of the card.
          if (response.status === 401 || response.status === 403) return
          failed = true
        } catch {
          failed = true
        }
      }
      const asList = <T>(data: unknown): T[] =>
        Array.isArray(data) ? (data as T[]) : []
      const asResults = <T>(data: unknown): T[] => {
        const results = (data as { results?: unknown })?.results
        return Array.isArray(results) ? (results as T[]) : []
      }

      await Promise.all([
        settle(
          mayProposals,
          () => getPendingProposals(team ?? null),
          (data) =>
            asList<{
              id: string
              lead_name?: string | null
              customer_name: string | null
              proposal_id: string
              updated: string
            }>(data).map((p) => ({
              id: `proposal:${p.id}`,
              kind: "proposal" as const,
              entity: p.lead_name || p.proposal_id,
              context: p.customer_name,
              since: p.updated,
              to: RoutesPaths.proposal.view(p.id)
            }))
        ),
        settle(
          mayContracts,
          () => getPendingContracts(team ?? null),
          (data) =>
            asList<{
              id: string
              lead_name?: string | null
              customer_name: string | null
              contract_id: string
              updated: string
            }>(data).map((c) => ({
              id: `contract:${c.id}`,
              kind: "contract" as const,
              entity: c.lead_name || c.contract_id,
              context: c.customer_name,
              since: c.updated,
              to: RoutesPaths.contract.view(c.id)
            }))
        ),
        settle(
          mayReviews,
          () => getMyPendingReviews(team),
          (data) =>
            asResults<{
              id: string
              file_name: string
              project_name: string
              created: string
              due_date: string | null
            }>(data).map((r) => ({
              id: `review:${r.id}`,
              kind: "review" as const,
              entity: r.file_name,
              context: r.project_name,
              since: r.due_date ?? r.created,
              to: RoutesPaths.document_reviews.view(r.id)
            }))
        ),
        settle(
          true,
          () =>
            getTracking({
              tile: "out_for_signature",
              page: 1,
              page_size: 5,
              ordering: "sent",
              ...(team ? { team } : {})
            }),
          (data) =>
            asResults<{
              id: string
              subject: { name: string }
              record: { name: string | null; customer_name: string | null }
              sent: string | null
              waiting_since: string | null
            }>(data).map((e) => ({
              id: `signature:${e.id}`,
              kind: "signature" as const,
              entity: e.subject?.name ?? "",
              context: e.record?.name ?? e.record?.customer_name ?? null,
              since: e.waiting_since ?? e.sent,
              to: RoutesPaths.signatureRequests
            }))
        )
      ])

      if (cancelled || id !== requestRef.current) return

      // Oldest-waiting first: the thing that has been sitting longest is the
      // one to act on. Undated rows go last rather than first.
      collected.sort((a, b) => {
        if (!a.since) return 1
        if (!b.since) return -1
        return a.since < b.since ? -1 : a.since > b.since ? 1 : 0
      })
      setRows(collected.slice(0, LIMIT))
      setTotal(collected.length)
      // A source that failed with nothing collected is an error, not "all
      // caught up". With something collected the card shows what it has and
      // the failure is a smaller lie than an empty state would be.
      setState(collected.length === 0 ? (failed ? "error" : "empty") : "ready")
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [branchId, mayProposals, mayContracts, mayReviews, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  return { rows, total, state, retry }
}
